import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useLang } from '../LanguageContext';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../utils/authFetch';
import '../styles/admin-pages.css';
import { isGlobalRole, isFarmLeadRole, normalizeRole } from '../utils/roles';

function farmIdOf(user) {
  const f = user?.farmId;
  if (!f) return '';
  if (typeof f === 'object' && f._id) return String(f._id);
  return String(f);
}

function farmLabel(user) {
  const f = user?.farmId;
  if (f && typeof f === 'object' && f.name) return f.name;
  return '—';
}

export default function UsersPage() {
  const { t } = useLang();
  const { token, user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [farms, setFarms] = useState([]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('worker');
  const [newUserFarmId, setNewUserFarmId] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState('worker');
  const [editPassword, setEditPassword] = useState('');
  const [farmBusyId, setFarmBusyId] = useState(null);

  const roleName = normalizeRole(user?.role);
  const canManage = isGlobalRole(roleName) || isFarmLeadRole(roleName);
  const canGenerateInvite = roleName === 'farm_owner' || isGlobalRole(roleName);
  const canSelectFarm = isGlobalRole(roleName);
  const ownFarmId = farmIdOf(user);
  const effectiveNewUserFarmId = canSelectFarm ? newUserFarmId : ownFarmId;
  const [inviteLink, setInviteLink] = useState('');

  const handleUnauthorized = useCallback(() => {
    logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  const fetchUsers = useCallback(async () => {
    if (!token || !canManage) return;
    try {
      const res = await authFetch('/api/admin/users', {}, { token, onUnauthorized: handleUnauthorized });
      if (!res.ok) return;
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch users', err);
    }
  }, [token, canManage, handleUnauthorized]);

  const fetchFarms = useCallback(async () => {
    if (!token || !canManage) return;
    try {
      const res = await authFetch('/api/farms', {}, { token, onUnauthorized: handleUnauthorized });
      if (!res.ok) return;
      const data = await res.json();
      setFarms(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch farms', err);
    }
  }, [token, canManage, handleUnauthorized]);

  useEffect(() => {
    if (ready && token && canManage) {
      fetchUsers();
      fetchFarms();
    }
  }, [ready, token, canManage, fetchUsers, fetchFarms]);

  useEffect(() => {
    if (canSelectFarm) {
      if (farms.length && !newUserFarmId) {
        setNewUserFarmId(String(farms[0]._id));
      }
      return;
    }
    setNewUserFarmId(ownFarmId || '');
  }, [canSelectFarm, farms, newUserFarmId, ownFarmId]);

  if (!ready) {
    return (
      <div className="container mt-3">
        <span className="spinner-border spinner-border-sm me-2" />
        {t('farmsAdmin.loading')}
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!canManage) {
    return (
      <div className="container mt-3">
        <div className="alert alert-warning">{t('farmsAdmin.usersAccessDenied')}</div>
      </div>
    );
  }

  const addUser = async (e) => {
    e.preventDefault();
    if (!effectiveNewUserFarmId) return;
    try {
      const res = await authFetch(
        '/api/admin/users',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: username,
            password,
            role,
            farmId: effectiveNewUserFarmId,
          }),
        },
        { token, onUnauthorized: handleUnauthorized }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        console.error(data.error || 'create failed');
        return;
      }
      setUsername('');
      setPassword('');
      setRole('worker');
      if (canSelectFarm) setNewUserFarmId('');
      fetchUsers();
    } catch (err) {
      console.error('Failed to add user', err);
    }
  };

  const deleteUser = async (id) => {
    await authFetch(`/api/admin/users/${id}`, { method: 'DELETE' }, { token, onUnauthorized: handleUnauthorized });
    fetchUsers();
  };

  const startEdit = (u) => {
    setEditId(u._id);
    setEditName(u.email);
    setEditRole(u.role || 'worker');
    setEditPassword('');
  };

  const saveEdit = async () => {
    if (!editId) return;
    const body = { email: editName, role: editRole };
    if (editPassword) body.password = editPassword;
    await authFetch(
      `/api/admin/users/${editId}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      { token, onUnauthorized: handleUnauthorized }
    );
    setEditId(null);
    setEditName('');
    setEditPassword('');
    setEditRole('worker');
    fetchUsers();
  };

  const generateInvite = async () => {
    try {
      const payload = { origin: window.location.origin };
      if (effectiveNewUserFarmId) payload.farmId = effectiveNewUserFarmId;
      const res = await authFetch(
        '/api/admin/users/invite-link',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
        { token, onUnauthorized: handleUnauthorized }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.error(data.error || 'invite failed');
        return;
      }
      setInviteLink(data.inviteUrl || '');
    } catch (err) {
      console.error('Failed to generate invite link', err);
    }
  };

  const changeUserFarm = async (u, nextFarmId) => {
    if (!canSelectFarm) return;
    const prev = farmIdOf(u);
    if (String(nextFarmId) === prev) return;
    setFarmBusyId(u._id);
    try {
      await authFetch(
        `/api/admin/users/${u._id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ farmId: nextFarmId }),
        },
        { token, onUnauthorized: handleUnauthorized }
      );
      fetchUsers();
    } catch (err) {
      console.error(err);
    } finally {
      setFarmBusyId(null);
    }
  };

  return (
    <div className="container-fluid px-4 py-3 admin-page users-page">
      <div className="users-page__inner">
      <h1 className="mb-3">{t('users')}</h1>
      <div className="row g-4 users-page__grid">
        <div className="col-12 col-xl-4">
          <form onSubmit={addUser} className="card card-body admin-form-card users-page__form-card">
            <h2 className="card-title h5 mb-3">{t('addUser')}</h2>
            <div className="row g-2">
              <div className="col-12">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('username')}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>
              <div className="col-12">
                <input
                  type="password"
                  className="form-control"
                  placeholder={t('password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <div className="col-12">
                <select className="form-select" value={role} onChange={(e) => setRole(e.target.value)}>
                  <option value="worker">worker</option>
                  <option value="farm_manager">farm_manager</option>
                  <option value="farm_owner">farm_owner</option>
                  {isGlobalRole(roleName) ? <option value="superuser">superuser</option> : null}
                  {isGlobalRole(roleName) ? <option value="admin">admin</option> : null}
                </select>
              </div>
              {canSelectFarm ? (
                <div className="col-12">
                  <select
                    className="form-select"
                    value={newUserFarmId}
                    onChange={(e) => setNewUserFarmId(e.target.value)}
                    required
                  >
                    {farms.length === 0 ? (
                      <option value="">{t('farmsAdmin.empty')}</option>
                    ) : (
                      farms.map((f) => (
                        <option key={String(f._id)} value={String(f._id)}>
                          {f.name}
                        </option>
                      ))
                    )}
                  </select>
                  <div className="form-text">{t('farmsAdmin.selectFarmNewUser')}</div>
                </div>
              ) : null}
              <div className="col-12">
                <button className="btn btn-primary" type="submit" disabled={!effectiveNewUserFarmId}>
                  {t('addUser')}
                </button>
              </div>
              {canGenerateInvite ? (
                <div className="col-12">
                  <button className="btn btn-outline-secondary" type="button" onClick={generateInvite}>
                    Generate worker invite link
                  </button>
                  {inviteLink ? (
                    <div className="mt-2">
                      <input
                        readOnly
                        className="form-control"
                        value={inviteLink}
                        onFocus={(e) => e.target.select()}
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </form>
        </div>

        <div className="col-12 col-xl-8">
      <div className="d-none d-md-block users-page__table-card">
        <h2 className="card-title h5 mb-3">{t('users')}</h2>
        <div className="table-responsive users-page__table-wrap">
        <table className="table table-striped align-middle mb-0">
          <thead>
            <tr>
              <th>{t('username')}</th>
              <th>{t('role')}</th>
              <th>{t('farmsAdmin.farmColumn')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u._id}>
                {editId === u._id ? (
                  <>
                    <td colSpan={4}>
                      <div className="d-flex flex-wrap gap-2 align-items-center p-2">
                        <input
                          type="text"
                          className="form-control"
                          style={{ minWidth: 200, maxWidth: 280 }}
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                        />
                        <input
                          type="password"
                          className="form-control"
                          style={{ minWidth: 160, maxWidth: 200 }}
                          placeholder={t('password')}
                          value={editPassword}
                          onChange={(e) => setEditPassword(e.target.value)}
                        />
                        <select
                          className="form-select"
                          style={{ width: 'auto' }}
                          value={editRole}
                          onChange={(e) => setEditRole(e.target.value)}
                        >
                          <option value="worker">worker</option>
                          <option value="farm_manager">farm_manager</option>
                          <option value="farm_owner">farm_owner</option>
                          {isGlobalRole(roleName) ? <option value="superuser">superuser</option> : null}
                          {isGlobalRole(roleName) ? <option value="admin">admin</option> : null}
                        </select>
                        <button type="button" className="btn btn-ios btn-ios--primary btn-sm" onClick={saveEdit}>
                          {t('save')}
                        </button>
                        <button type="button" className="btn btn-ios btn-ios--muted btn-sm" onClick={() => setEditId(null)}>
                          {t('cancel')}
                        </button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td>
                      <div className="fw-medium">{u.email}</div>
                      <small className="text-muted">{u.shortId || u._id}</small>
                    </td>
                    <td>{u.role}</td>
                    <td style={{ minWidth: 200 }}>
                      {(() => {
                        const fid = farmIdOf(u);
                        const list = [...farms];
                        if (fid && !list.some((f) => String(f._id) === fid)) {
                          list.unshift({ _id: fid, name: farmLabel(u) });
                        }
                        if (!canSelectFarm) return <span>{farmLabel(u)}</span>;
                        return (
                          <select
                            className="form-select form-select-sm"
                            value={fid || (list[0] ? String(list[0]._id) : '')}
                            disabled={farmBusyId === u._id || list.length === 0}
                            onChange={(e) => changeUserFarm(u, e.target.value)}
                          >
                            {list.map((f) => (
                              <option key={String(f._id)} value={String(f._id)}>
                                {f.name}
                              </option>
                            ))}
                          </select>
                        );
                      })()}
                    </td>
                    <td className="text-end text-nowrap">
                      <button type="button" className="btn btn-ios btn-ios--muted btn-sm me-1" onClick={() => startEdit(u)}>
                        {t('edit')}
                      </button>
                      <button type="button" className="btn btn-ios btn-ios--destructive btn-sm" onClick={() => deleteUser(u._id)}>
                        {t('delete')}
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <div className="d-md-none">
        {users.map((u) => {
          const fid = farmIdOf(u);
          const list = [...farms];
          if (fid && !list.some((f) => String(f._id) === fid)) {
            list.unshift({ _id: fid, name: farmLabel(u) });
          }
          return (
            <div key={u._id} className="admin-mobile-card">
              {editId === u._id ? (
                <div className="d-flex flex-column gap-2">
                  <input type="text" className="form-control" value={editName} onChange={(e) => setEditName(e.target.value)} />
                  <input
                    type="password"
                    className="form-control"
                    placeholder={t('password')}
                    value={editPassword}
                    onChange={(e) => setEditPassword(e.target.value)}
                  />
                  <select className="form-select" value={editRole} onChange={(e) => setEditRole(e.target.value)}>
                    <option value="worker">worker</option>
                    <option value="farm_manager">farm_manager</option>
                    <option value="farm_owner">farm_owner</option>
                    {isGlobalRole(roleName) ? <option value="superuser">superuser</option> : null}
                    {isGlobalRole(roleName) ? <option value="admin">admin</option> : null}
                  </select>
                  <div className="admin-mobile-card__actions">
                    <button type="button" className="btn btn-ios btn-ios--primary" onClick={saveEdit}>
                      {t('save')}
                    </button>
                    <button type="button" className="btn btn-ios btn-ios--muted" onClick={() => setEditId(null)}>
                      {t('cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="admin-mobile-card__label">{t('username')}</div>
                  <div className="fw-medium">{u.email}</div>
                  <small className="text-muted">{u.shortId || u._id}</small>
                  <div className="admin-mobile-card__label mt-2">{t('role')}</div>
                  <div>{u.role}</div>
                  <div className="admin-mobile-card__label mt-2">{t('farmsAdmin.farmColumn')}</div>
                  {canSelectFarm ? (
                    <select
                      className="form-select form-select-sm"
                      value={fid || (list[0] ? String(list[0]._id) : '')}
                      disabled={farmBusyId === u._id || list.length === 0}
                      onChange={(e) => changeUserFarm(u, e.target.value)}
                    >
                      {list.map((f) => (
                        <option key={String(f._id)} value={String(f._id)}>
                          {f.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div>{farmLabel(u)}</div>
                  )}
                  <div className="admin-mobile-card__actions">
                    <button type="button" className="btn btn-ios btn-ios--muted" onClick={() => startEdit(u)}>
                      {t('edit')}
                    </button>
                    <button type="button" className="btn btn-ios btn-ios--destructive" onClick={() => deleteUser(u._id)}>
                      {t('delete')}
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
        </div>
      </div>
      </div>
    </div>
  );
}
